import {Notification} from '@app/gen/schemas/notification';
import {NotificationData} from '@app/gen/schemas/notification-data';
import {notificationActionPath} from '@common/notifications/notification-destination';
import {NotificationsDialogContext} from '@common/notifications/notifications-dialog';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {FormattedRelativeTime} from '@ui/i18n/formatted-relative-time';
import {MixedImage} from '@ui/images/mixed-image';
import {isAbsoluteUrl} from '@ui/utils/urls/is-absolute-url';
import clsx from 'clsx';
import {JSXElementConstructor, use} from 'react';

type Props = {
  notification: Notification;
  line: NotificationData['lines'][number];
  index: number;
  iconRenderer?: JSXElementConstructor<{icon: string}>;
};

export function NotificationLine({
  notification,
  line,
  index,
  iconRenderer,
}: Props) {
  const isPrimary = line.type === 'primary' || index === 0;
  const Icon = iconRenderer || DefaultIconRenderer;
  const navigate = useNavigate();
  const dialog = use(NotificationsDialogContext);
  const rawAction =
    typeof line.action === 'string' ? line.action : line.action?.action;
  const dest = notificationActionPath(rawAction);

  const className = clsx(
    'flex items-center gap-2',
    dest && 'hover:underline',
    isPrimary
      ? 'text-foreground text-sm whitespace-nowrap'
      : 'mt-1.5 text-xs text-muted-foreground',
  );

  const body = (
    <>
      {line.icon && <Icon icon={line.icon} />}
      <div
        className="overflow-hidden text-ellipsis"
        dangerouslySetInnerHTML={{__html: line.content}}
      />
    </>
  );

  return (
    <>
      {dest ? (
        <button
          type="button"
          className={className}
          title={
            typeof line.action === 'object' ? line.action?.label : undefined
          }
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            dialog.setOpen(false);
            if (isAbsoluteUrl(dest)) {
              window.open(dest, '_blank')?.focus();
              return;
            }
            navigate(dest);
          }}
        >
          {body}
        </button>
      ) : (
        <div className={className}>{body}</div>
      )}
      {index === 0 && (
        <time className="text-xs text-muted-foreground">
          <FormattedRelativeTime date={notification.created_at} />
        </time>
      )}
    </>
  );
}

interface DefaultIconRendererProps {
  icon: string;
}
function DefaultIconRenderer({icon}: DefaultIconRendererProps) {
  return <MixedImage src={icon} />;
}
